/**
 * Records a voice note or a video from the device (MediaRecorder), as the CRM
 * chat window's voice overlay and "Record video" button do. One recording at
 * a time; switching chats or unmounting drops it and releases the devices.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type RecordKind = 'audio' | 'video';

export interface Recording {
  file: File;
  url: string;
  seconds: number;
}

function mimeFor(kind: RecordKind): string {
  const candidates = kind === 'video' ? ['video/webm;codecs=vp8,opus', 'video/webm'] : ['audio/webm;codecs=opus', 'audio/webm'];
  return candidates.find((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) || '';
}

export function useRecorder() {
  const [kind, setKind] = useState<RecordKind | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const finish = useRef<((r: Recording | null) => void) | null>(null);

  const release = useCallback(() => {
    rec.current?.stream.getTracks().forEach((t) => t.stop());
    rec.current = null;
    chunks.current = [];
    setStream(null);
    setKind(null);
    setSeconds(0);
  }, []);

  useEffect(() => {
    if (!kind) return;
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt.current) / 1000)), 250);
    return () => window.clearInterval(timer);
  }, [kind]);

  useEffect(
    () => () => {
      finish.current = null;
      if (rec.current?.state === 'recording') rec.current.stop();
      rec.current?.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  /** Ask for the mic/camera and start; throws when the browser refuses. */
  const start = useCallback(async (next: RecordKind) => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) throw new Error('unsupported');
    const media = await navigator.mediaDevices.getUserMedia(
      next === 'video' ? { video: true, audio: true } : { audio: { channelCount: 1, echoCancellation: false, noiseSuppression: false, autoGainControl: true } },
    );
    const mimeType = mimeFor(next);
    const recorder = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    recorder.addEventListener('dataavailable', (e) => e.data?.size && chunks.current.push(e.data));
    recorder.addEventListener('stop', () => {
      const type = mimeType || (next === 'video' ? 'video/webm' : 'audio/webm');
      const blob = new Blob(chunks.current, { type });
      const secs = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
      const done = finish.current;
      finish.current = null;
      release();
      if (!done) return;
      if (!blob.size) return done(null);
      const file = new File([blob], `${next === 'video' ? 'video' : 'voice'}-${Date.now()}.webm`, { type: blob.type });
      done({ file, url: URL.createObjectURL(blob), seconds: secs });
    });
    rec.current = recorder;
    startedAt.current = Date.now();
    recorder.start(250);
    setSeconds(0);
    setKind(next);
    setStream(media);
  }, [release]);

  /** Stop and hand back the recording (null when nothing was captured). */
  const stop = useCallback(
    () =>
      new Promise<Recording | null>((resolve) => {
        const recorder = rec.current;
        if (!recorder || recorder.state !== 'recording') return resolve(null);
        finish.current = resolve;
        try {
          recorder.requestData?.();
        } catch {
          /* not every browser allows it here */
        }
        recorder.stop();
      }),
    [],
  );

  /** Stop and throw the recording away. */
  const cancel = useCallback(() => {
    finish.current = null;
    if (rec.current?.state === 'recording') rec.current.stop();
    else release();
  }, [release]);

  return { kind, seconds, stream, start, stop, cancel };
}

export function formatSeconds(total: number): string {
  const s = Math.max(0, Math.floor(total));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
